<template>
  <div class="personage-page">
    <!-- 顶部导航提示 -->
    <div class="page-header">
      <el-breadcrumb separator="/">
        <el-breadcrumb-item :to="{ path: '/index/all' }">首页</el-breadcrumb-item>
        <el-breadcrumb-item>个人中心</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <div class="page-body">
      <!-- ======== 左列：资料卡片 + 统计 ======== -->
      <div class="left-column">
        <!-- 资料卡片 -->
        <el-card shadow="never" class="profile-card">
          <div class="profile-header">
            <div class="avatar-wrapper">
              <AvatarUpload v-model="avatarUrl" @input="onAvatarChange" />
              <div class="avatar-badge" v-if="user.type === 2">
                <el-tag size="mini" type="danger" effect="dark">管理员</el-tag>
              </div>
            </div>
            <h2 class="profile-name">{{ user.name || user.userName || '未设置昵称' }}</h2>
            <p class="profile-account">账号：{{ user.account }}</p>
            <el-tag :type="user.status === 1 ? 'success' : 'danger'" size="small" effect="plain">
              {{ user.status === 1 ? '● 正常' : '● 停用' }}
            </el-tag>
          </div>

          <el-divider />

          <div class="profile-detail">
            <div class="detail-item">
              <span class="label"><i class="el-icon-phone-outline"></i> 手机号</span>
              <span class="value">{{ user.phone || '未设置' }}</span>
            </div>
            <div class="detail-item">
              <span class="label"><i class="el-icon-message"></i> 邮箱</span>
              <span class="value">{{ user.email || '未设置' }}</span>
            </div>
            <div class="detail-item">
              <span class="label"><i class="el-icon-user"></i> 用户类型</span>
              <el-tag :type="user.type === 2 ? 'danger' : 'primary'" size="mini">
                {{ user.type === 2 ? '管理员' : '普通用户' }}
              </el-tag>
            </div>
            <div class="detail-item">
              <span class="label"><i class="el-icon-date"></i> 创建时间</span>
              <span class="value">{{ formatDate(user.createTime) }}</span>
            </div>
            <div class="detail-item" v-if="user.lastLoginTime">
              <span class="label"><i class="el-icon-time"></i> 最近登录</span>
              <span class="value">{{ formatDate(user.lastLoginTime) }}</span>
            </div>
            <div class="detail-item">
              <span class="label"><i class="el-icon-s-custom"></i> 用户名</span>
              <span class="value">{{ user.userName || '未设置' }}</span>
            </div>
            <div class="detail-item" v-if="user.sex">
              <span class="label"><i class="el-icon-male"></i> 性别</span>
              <span class="value">{{ user.sex === 1 ? '男' : '女' }}</span>
            </div>
          </div>
        </el-card>

        <!-- 快速统计卡片 -->
        <el-card shadow="never" class="stat-card">
          <div slot="header">
            <span><i class="el-icon-data-line"></i> 数据概览</span>
          </div>
          <div class="stat-grid">
            <div class="stat-item" @click="jumpTo('task')">
              <div class="stat-num">{{ statData.taskCount ?? '-' }}</div>
              <div class="stat-label">我的任务</div>
            </div>
            <div class="stat-item" @click="jumpTo('plan')">
              <div class="stat-num">{{ statData.planCount ?? '-' }}</div>
              <div class="stat-label">我的计划</div>
            </div>
            <div class="stat-item" @click="jumpTo('document')">
              <div class="stat-num">{{ statData.docCount ?? '-' }}</div>
              <div class="stat-label">我的文档</div>
            </div>
            <div class="stat-item" @click="jumpTo('collect')">
              <div class="stat-num">{{ statData.collectCount ?? '-' }}</div>
              <div class="stat-label">我的收藏</div>
            </div>
          </div>
        </el-card>
      </div>

      <!-- ======== 右列：Tabs 操作区 ======== -->
      <div class="right-column">
        <el-card shadow="never" class="content-card">
          <el-tabs v-model="activeTab" @tab-click="onTabClick">
            <!-- Tab 1: 编辑基本信息 -->
            <el-tab-pane label="✏️ 编辑信息" name="info">
              <div class="tab-intro">
                <p class="intro-text">修改您的个人资料信息，带 <span class="required-star">*</span> 为必填项。</p>
              </div>
              <el-form
                ref="infoForm"
                :model="infoForm"
                :rules="infoRules"
                label-width="110px"
                class="info-form"
              >
                <el-row :gutter="20">
                  <el-col :span="12">
                    <el-form-item label="用户名" prop="userName">
                      <el-input v-model="infoForm.userName" placeholder="登录用的用户名" />
                    </el-form-item>
                  </el-col>
                  <el-col :span="12">
                    <el-form-item label="显示名称" prop="name">
                      <el-input v-model="infoForm.name" placeholder="对外展示的昵称">
                        <i slot="prefix" class="el-icon-edit" />
                      </el-input>
                    </el-form-item>
                  </el-col>
                </el-row>

                <el-row :gutter="20">
                  <el-col :span="12">
                    <el-form-item label="手机号" prop="phone">
                      <el-input v-model="infoForm.phone" placeholder="请输入手机号" maxlength="11" />
                    </el-form-item>
                  </el-col>
                  <el-col :span="12">
                    <el-form-item label="邮箱" prop="email">
                      <el-input v-model="infoForm.email" placeholder="请输入邮箱地址" />
                    </el-form-item>
                  </el-col>
                </el-row>

                <el-row :gutter="20">
                  <el-col :span="12">
                    <el-form-item label="性别" prop="sex">
                      <el-radio-group v-model="infoForm.sex">
                        <el-radio :label="1">男</el-radio>
                        <el-radio :label="2">女</el-radio>
                        <el-radio :label="0">保密</el-radio>
                      </el-radio-group>
                    </el-form-item>
                  </el-col>
                  <el-col :span="12">
                    <el-form-item label="生日" prop="birthday">
                      <el-date-picker
                        v-model="infoForm.birthday"
                        type="date"
                        placeholder="选择日期"
                        value-format="timestamp"
                        style="width: 100%"
                      />
                    </el-form-item>
                  </el-col>
                </el-row>

                <el-form-item label="个人简介" prop="intro">
                  <el-input
                    v-model="infoForm.intro"
                    type="textarea"
                    :rows="3"
                    maxlength="200"
                    show-word-limit
                    placeholder="一句话介绍自己（最多200字）"
                  />
                </el-form-item>

                <el-divider />

                <el-form-item>
                  <el-button type="primary" :loading="infoLoading" @click="submitInfo" icon="el-icon-check">
                    保存修改
                  </el-button>
                  <el-button @click="resetInfoForm" icon="el-icon-refresh">重置</el-button>
                </el-form-item>
              </el-form>
            </el-tab-pane>

            <!-- Tab 2: 修改密码 -->
            <el-tab-pane label="🔒 修改密码" name="password">
              <div class="tab-intro">
                <p class="intro-text">定期更换密码可以提高账户安全性，新密码长度需为 6~20 位字符。</p>
              </div>
              <el-form
                ref="pwdForm"
                :model="pwdForm"
                :rules="pwdRules"
                label-width="120px"
                class="pwd-form"
              >
                <el-form-item label="原密码" prop="oldPassword">
                  <el-input
                    v-model="pwdForm.oldPassword"
                    type="password"
                    show-password
                    placeholder="请输入原密码"
                    prefix-icon="el-icon-lock"
                  />
                </el-form-item>
                <el-form-item label="新密码" prop="newPassword">
                  <el-input
                    v-model="pwdForm.newPassword"
                    type="password"
                    show-password
                    placeholder="6~20 位字符，包含字母和数字更安全"
                    prefix-icon="el-icon-key"
                  />
                  <div class="pwd-strength" v-if="pwdForm.newPassword">
                    <span class="strength-label">密码强度：</span>
                    <span :class="pwdStrengthClass">{{ pwdStrengthText }}</span>
                  </div>
                </el-form-item>
                <el-form-item label="确认新密码" prop="confirmPassword">
                  <el-input
                    v-model="pwdForm.confirmPassword"
                    type="password"
                    show-password
                    placeholder="请再次输入新密码"
                    prefix-icon="el-icon-circle-check"
                  />
                </el-form-item>

                <el-divider />

                <el-form-item>
                  <el-button type="primary" :loading="pwdLoading" @click="submitPassword" icon="el-icon-upload2">
                    确认修改
                  </el-button>
                  <el-button @click="resetPwdForm" icon="el-icon-refresh">重置</el-button>
                </el-form-item>
              </el-form>
            </el-tab-pane>

            <!-- Tab 3: 登录日志 -->
            <el-tab-pane label="📋 登录日志" name="loginLog">
              <div class="tab-intro">
                <p class="intro-text">最近 10 次登录记录，如有异常请及时修改密码。</p>
              </div>
              <el-table
                :data="loginLogs"
                stripe
                size="small"
                v-loading="logLoading"
                empty-text="暂无登录记录"
              >
                <el-table-column label="序号" type="index" width="60" align="center" />
                <el-table-column label="登录时间" width="180" align="center">
                  <template slot-scope="scope">
                    {{ formatDate(scope.row.createTime) }}
                  </template>
                </el-table-column>
                <el-table-column label="登录 IP" prop="ip" min-width="150" align="center">
                  <template slot-scope="scope">
                    <code>{{ scope.row.ip || '未知' }}</code>
                  </template>
                </el-table-column>
                <el-table-column label="登录地点" prop="location" min-width="150" align="center">
                  <template slot-scope="scope">
                    {{ scope.row.location || '未知' }}
                  </template>
                </el-table-column>
                <el-table-column label="设备" prop="userAgent" min-width="200">
                  <template slot-scope="scope">
                    <el-tooltip :content="scope.row.userAgent || ''" placement="top">
                      <span class="ua-text">{{ formatUA(scope.row.userAgent) }}</span>
                    </el-tooltip>
                  </template>
                </el-table-column>
              </el-table>
            </el-tab-pane>
          </el-tabs>
        </el-card>
      </div>
    </div>
  </div>
</template>

<script>
import { updateUser, updatePassword, userList } from '@/api/user'
import AvatarUpload from '@/components/AvatarUpload.vue'

export default {
  name: 'PersonagePage',
  components: { AvatarUpload },
  data() {
    // 自定义校验：确认密码
    const validateConfirm = (rule, value, callback) => {
      if (value !== this.pwdForm.newPassword) {
        callback(new Error('两次输入的密码不一致'))
      } else {
        callback()
      }
    }

    return {
      activeTab: 'info',

      // ---------- 头像 ----------
      avatarUrl: '',

      // ---------- 编辑信息表单 ----------
      infoForm: {
        userName: '',
        name: '',
        phone: '',
        email: '',
        sex: 0,
        birthday: null,
        intro: ''
      },
      infoRules: {
        userName: [
          { required: true, message: '请输入用户名', trigger: 'blur' },
          { min: 2, max: 20, message: '用户名长度 2~20 位', trigger: 'blur' }
        ],
        phone: [
          {
            pattern: /^1[3-9]\d{9}$/,
            message: '手机号格式不正确',
            trigger: 'blur'
          }
        ],
        email: [
          {
            type: 'email',
            message: '邮箱格式不正确',
            trigger: 'blur'
          }
        ]
      },
      infoLoading: false,

      // ---------- 修改密码表单 ----------
      pwdForm: {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      pwdRules: {
        oldPassword: [{ required: true, message: '请输入原密码', trigger: 'blur' }],
        newPassword: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, max: 20, message: '密码长度为 6~20 位', trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: '请确认新密码', trigger: 'blur' },
          { validator: validateConfirm, trigger: 'blur' }
        ]
      },
      pwdLoading: false,

      // ---------- 数据统计 ----------
      statData: {
        taskCount: null,
        planCount: null,
        docCount: null,
        collectCount: null
      },

      // ---------- 登录日志 ----------
      loginLogs: [],
      logLoading: false
    }
  },

  computed: {
    user() {
      return this.$store.getters.userInfo
    },

    // 密码强度
    pwdStrengthText() {
      const pwd = this.pwdForm.newPassword || ''
      const score = this.calcPwdScore(pwd)
      if (score < 2) return '弱'
      if (score < 4) return '中'
      return '强'
    },
    pwdStrengthClass() {
      const pwd = this.pwdForm.newPassword || ''
      const score = this.calcPwdScore(pwd)
      if (score < 2) return 'strength-weak'
      if (score < 4) return 'strength-mid'
      return 'strength-strong'
    }
  },

  watch: {
    user: {
      immediate: true,
      handler(val) {
        if (val) {
          this.avatarUrl = val.headImage || ''
          this.infoForm.userName = val.userName || ''
          this.infoForm.name = val.name || ''
          this.infoForm.phone = val.phone || ''
          this.infoForm.email = val.email || ''
          this.infoForm.sex = val.sex || 0
          this.infoForm.birthday = val.birthday || null
          this.infoForm.intro = val.intro || ''
        }
      }
    }
  },

  created() {
    this.fetchStatData()
    this.fetchLoginLogs()
  },

  methods: {
    formatDate(timestamp) {
      if (!timestamp) return ''
      // 如果是秒级时间戳则转换
      const ts = timestamp > 1e12 ? timestamp : timestamp * 1000
      const d = new Date(ts)
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    },

    formatUA(ua) {
      if (!ua) return '未知'
      if (ua.includes('Windows')) return 'Windows'
      if (ua.includes('Mac OS')) return 'macOS'
      if (ua.includes('Linux')) return 'Linux'
      if (ua.includes('Android')) return 'Android'
      if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS'
      // 简单截取前一段
      return ua.substring(0, 40) + (ua.length > 40 ? '...' : '')
    },

    calcPwdScore(pwd) {
      if (!pwd) return 0
      let score = 0
      if (pwd.length >= 8) score++
      if (pwd.length >= 12) score++
      if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score++
      if (/\d/.test(pwd)) score++
      if (/[^a-zA-Z0-9]/.test(pwd)) score++
      return score
    },

    // ---------- 头像 ----------
    onAvatarChange(url) {
      this.avatarUrl = url
      const payload = { id: this.user.id, headImage: url }
      updateUser(payload).then(() => {
        const updated = { ...this.user, headImage: url }
        this.$store.dispatch('login', updated)
        this.$message.success('头像已更新')
      }).catch(() => {
        this.$message.error('头像更新失败')
      })
    },

    // ---------- 获取统计 ----------
    async fetchStatData() {
      try {
        // 可以用专门的统计接口（如果后端有），这里通过现有接口模拟
        // 实际项目中建议后端提供专用的用户统计接口
        const res = await userList({ page: 1, limit: 1, userId: this.user.id })
        if (res && res.data) {
          // 如果后端返回了统计字段就直接使用，否则显示占位
          this.statData = {
            taskCount: res.data.taskCount ?? res.data.taskCount ?? 0,
            planCount: res.data.planCount ?? res.data.planCount ?? 0,
            docCount: res.data.docCount ?? res.data.docCount ?? 0,
            collectCount: res.data.collectCount ?? res.data.collectCount ?? 0
          }
        }
      } catch (e) {
        // 静默失败，保持占位显示
      }
    },

    // ---------- 获取登录日志 ----------
    async fetchLoginLogs() {
      this.logLoading = true
      try {
        const res = await userList({ page: 1, limit: 10, userId: this.user.id })
        if (res && res.data && res.data.loginLogs) {
          this.loginLogs = res.data.loginLogs
        }
      } catch (e) {
        // 静默
      } finally {
        this.logLoading = false
      }
    },

    // ---------- Tab 切换 ----------
    onTabClick(tab) {
      if (tab.name === 'loginLog') {
        this.fetchLoginLogs()
      }
    },

    // ---------- 编辑信息 ----------
    submitInfo() {
      this.$refs.infoForm.validate(valid => {
        if (!valid) return
        this.infoLoading = true

        const payload = {
          id: this.user.id,
          userName: this.infoForm.userName,
          name: this.infoForm.name,
          phone: this.infoForm.phone,
          email: this.infoForm.email,
          sex: this.infoForm.sex,
          birthday: this.infoForm.birthday,
          intro: this.infoForm.intro
        }

        updateUser(payload).then(res => {
          if (res && (res.code === 200 || res.success === true)) {
            const updated = {
              ...this.user,
              userName: this.infoForm.userName,
              name: this.infoForm.name,
              phone: this.infoForm.phone,
              email: this.infoForm.email,
              sex: this.infoForm.sex,
              birthday: this.infoForm.birthday,
              intro: this.infoForm.intro
            }
            this.$store.dispatch('login', updated)
            this.$message.success('信息修改成功')
          } else {
            this.$message.error(res?.msg || '修改失败')
          }
        }).catch(() => {
          this.$message.error('网络异常，请稍后重试')
        }).finally(() => {
          this.infoLoading = false
        })
      })
    },

    resetInfoForm() {
      this.$refs.infoForm.resetFields()
      const val = this.user
      this.infoForm.userName = val.userName || ''
      this.infoForm.name = val.name || ''
      this.infoForm.phone = val.phone || ''
      this.infoForm.email = val.email || ''
      this.infoForm.sex = val.sex || 0
      this.infoForm.birthday = val.birthday || null
      this.infoForm.intro = val.intro || ''
    },

    // ---------- 修改密码 ----------
    submitPassword() {
      this.$refs.pwdForm.validate(valid => {
        if (!valid) return
        this.pwdLoading = true

        const payload = {
          id: this.user.id,
          oldPassword: this.pwdForm.oldPassword,
          newPassword: this.pwdForm.newPassword
        }

        updatePassword(payload).then(res => {
          if (res && (res.code === 200 || res.success === true)) {
            this.$message.success('密码修改成功，请重新登录')
            this.$refs.pwdForm.resetFields()
            this.$store.dispatch('logout')
            this.$router.push('/login')
          } else {
            this.$message.error(res?.msg || '密码修改失败')
          }
        }).catch(() => {
          this.$message.error('网络异常，请稍后重试')
        }).finally(() => {
          this.pwdLoading = false
        })
      })
    },

    resetPwdForm() {
      this.$refs.pwdForm.resetFields()
    },

    // ---------- 跳转导航 ----------
    jumpTo(type) {
      const pathMap = {
        task: '/index/task',
        plan: '/index/plan',
        document: '/index/document',
        collect: '/index/collect'
      }
      const path = pathMap[type]
      if (path) {
        window.open('/#' + path)
      }
    }
  }
}
</script>

<style scoped>
/* ========= 整体布局 ========= */
.personage-page {
  background: #f0f2f5;
  min-height: calc(100vh - 80px);
  padding: 16px 20px;
}

.page-header {
  margin-bottom: 16px;
}

.page-body {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

/* ========= 左列 ========= */
.left-column {
  width: 340px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ========= 右列 ========= */
.right-column {
  flex: 1;
  min-width: 0;
}

/* ========= 资料卡片 ========= */
.profile-card {
  border-radius: 12px;
  overflow: hidden;
}

.profile-header {
  text-align: center;
  padding: 20px 0 0;
}

.avatar-wrapper {
  display: inline-block;
  margin-bottom: 4px;
  position: relative;
}

.avatar-badge {
  position: absolute;
  bottom: 0;
  right: -8px;
}

.profile-name {
  margin: 10px 0 4px;
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}

.profile-account {
  margin: 4px 0;
  color: #909399;
  font-size: 13px;
}

.profile-detail {
  padding: 0 12px 12px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-item .label {
  color: #909399;
  font-size: 13px;
}

.detail-item .label i {
  margin-right: 4px;
}

.detail-item .value {
  color: #303133;
  font-weight: 500;
  max-width: 160px;
  word-break: break-all;
  text-align: right;
}

/* ========= 统计卡片 ========= */
.stat-card {
  border-radius: 12px;
  overflow: hidden;
}

.stat-card >>> .el-card__header {
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.stat-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 4px 0;
}

.stat-item {
  text-align: center;
  padding: 12px 0;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
}

.stat-item:hover {
  background: #f5f7fa;
}

.stat-num {
  font-size: 24px;
  font-weight: 700;
  color: #409eff;
  line-height: 1.4;
}

.stat-label {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

/* ========= 内容卡片 ========= */
.content-card {
  border-radius: 12px;
  overflow: hidden;
  min-height: 480px;
}

.content-card >>> .el-card__body {
  padding: 20px 24px;
}

.tab-intro {
  margin-bottom: 20px;
}

.intro-text {
  color: #606266;
  font-size: 13px;
  margin: 0;
}

.required-star {
  color: #f56c6c;
}

/* ========= 表单 ========= */
.info-form,
.pwd-form {
  max-width: 720px;
}

/* ========= 密码强度指示器 ========= */
.pwd-strength {
  margin-top: 6px;
  font-size: 12px;
}

.strength-label {
  color: #909399;
}

.strength-weak {
  color: #f56c6c;
  font-weight: 600;
}

.strength-mid {
  color: #e6a23c;
  font-weight: 600;
}

.strength-strong {
  color: #67c23a;
  font-weight: 600;
}

/* ========= 登录日志表格 ========= */
.ua-text {
  max-width: 200px;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

code {
  background: #f5f7fa;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 12px;
  color: #606266;
}

/* ========= 响应式 ========= */
@media screen and (max-width: 900px) {
  .page-body {
    flex-direction: column;
  }
  .left-column {
    width: 100%;
  }
}
</style>
